import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useToast } from './ToastContext';

const SubscriptionContext = createContext(null);

const DEFAULT_INTERVAL_SUMMARY = { price: 0, salePrice: null, effectivePrice: 0 };
const DEFAULT_PLAN = {
  code: 'pro',
  name: 'Pro',
  currency: 'VND',
  saleLabel: null,
  saleActive: false,
  paymentsAvailable: false,
  monthly: DEFAULT_INTERVAL_SUMMARY,
  yearly: DEFAULT_INTERVAL_SUMMARY,
  triennial: DEFAULT_INTERVAL_SUMMARY,
};
const DEFAULT_USAGE = { budgetsByType: {}, walletsCount: 0, aiCreditsUsedToday: 0 };
const DEFAULT_LIMITS = { budgetsPerType: 2, wallets: 2, aiCreditsPerDay: 5 };

export function SubscriptionProvider({ children }) {
  const { isReady, isAuthenticated, refreshMe } = useAuth();
  const { addToast } = useToast();
  const { t } = useTranslation('subscription');
  const [plan, setPlan] = useState(DEFAULT_PLAN);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(DEFAULT_USAGE);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [renewalDue, setRenewalDue] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState(null);
  const [adminPayments, setAdminPayments] = useState([]);
  const canFetch = isReady && isAuthenticated;

  const openUpgradeModal = useCallback((reason) => setUpgradeReason(reason || 'default'), []);
  const closeUpgradeModal = useCallback(() => setUpgradeReason(null), []);

  const refreshPlan = useCallback(async () => {
    try {
      const { plan: fetchedPlan } = await api.get('/subscriptions/plan');
      setPlan(fetchedPlan);
    } catch (error) {
      setPlan(DEFAULT_PLAN);
    }
  }, []);

  const refreshMine = useCallback(async () => {
    if (!canFetch) return;
    try {
      const { subscription: fetchedSubscription, usage: fetchedUsage, limits: fetchedLimits, renewalDue: fetchedRenewalDue } = await api.get('/subscriptions/me');
      setSubscription(fetchedSubscription);
      setUsage(fetchedUsage);
      setLimits(fetchedLimits);
      setRenewalDue(!!fetchedRenewalDue);
    } catch (error) {
      setSubscription(null);
      setUsage(DEFAULT_USAGE);
      setRenewalDue(false);
    }
  }, [canFetch]);

  useEffect(() => {
    refreshPlan();
  }, [refreshPlan]);

  useEffect(() => {
    if (!canFetch) {
      setSubscription(null);
      setUsage(DEFAULT_USAGE);
      return;
    }
    refreshMine();
  }, [canFetch, refreshMine]);

  // Surfaces the renewal deadline as an actual notification (toast + bell, both from
  // ToastContext) instead of something only visible if the user happens to open
  // Settings. Capped to once per calendar day per subscription via localStorage so it
  // doesn't re-fire on every page nav/refresh while still repeating daily as a reminder.
  useEffect(() => {
    if (!renewalDue || !subscription) return;
    const today = new Date().toISOString().slice(0, 10);
    const storageKey = `zlux-renewal-toast-${subscription.id}-${today}`;
    if (localStorage.getItem(storageKey)) return;
    localStorage.setItem(storageKey, '1');
    addToast(t('billing.renewalReminderToast', { date: new Date(subscription.currentPeriodEnd).toLocaleDateString() }), 'warning');
  }, [renewalDue, subscription, addToast, t]);

  // Bank-transfer (VietQR) checkout is not an in-page charge: this only creates a
  // pending Payment and gets back a QR to show. The subscription itself activates later,
  // out of band, once /sepay/webhook confirms a matching transfer - see UpgradeModal's
  // polling loop (checkPaymentStatus) for how the UI notices that happened.
  const checkout = useCallback(async (interval) => {
    try {
      const payment = await api.post('/subscriptions/checkout', { interval });
      return { success: true, payment };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const checkPaymentStatus = useCallback(async (paymentId) => {
    const { status } = await api.get(`/subscriptions/checkout/${paymentId}/status`);
    return status;
  }, []);

  // Opt-in, offered once a checkout succeeds - separate from checkout itself since not
  // every user tracking their spending wants their Pro payment folded in automatically.
  const saveCheckoutAsExpense = useCallback(async (paymentId) => {
    try {
      const { expense } = await api.post(`/subscriptions/checkout/${paymentId}/save-as-expense`, {});
      return { success: true, expense };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  // Called once polling (above) sees a payment flip to "succeeded".
  const syncAfterPayment = useCallback(async () => {
    await Promise.all([refreshMine(), refreshMe()]);
  }, [refreshMine, refreshMe]);

  const cancelSubscription = useCallback(async () => {
    try {
      const { subscription: updated } = await api.post('/subscriptions/cancel', {});
      setSubscription(updated);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const adminUpdatePlan = useCallback(async (updates) => {
    try {
      const { plan: updatedPlan } = await api.patch('/subscriptions/admin/plan', updates);
      setPlan(updatedPlan);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const refreshAdminPayments = useCallback(async () => {
    try {
      const { payments } = await api.get('/subscriptions/admin/payments');
      setAdminPayments(payments);
    } catch (error) {
      setAdminPayments([]);
    }
  }, []);

  const fetchUserSubscription = useCallback(async (userId) => api.get(`/subscriptions/admin/user/${userId}`), []);

  const value = useMemo(() => ({
    plan, subscription, usage, limits, renewalDue,
    refreshPlan, refreshMine, checkout, checkPaymentStatus, saveCheckoutAsExpense, syncAfterPayment, cancelSubscription, adminUpdatePlan,
    upgradeReason, openUpgradeModal, closeUpgradeModal,
    adminPayments, refreshAdminPayments, fetchUserSubscription,
  }), [
    plan, subscription, usage, limits, renewalDue, refreshPlan, refreshMine, checkout, checkPaymentStatus, saveCheckoutAsExpense, syncAfterPayment, cancelSubscription, adminUpdatePlan,
    upgradeReason, openUpgradeModal, closeUpgradeModal, adminPayments, refreshAdminPayments, fetchUserSubscription,
  ]);

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}
